import React from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Chip, IconButton, Stack, Typography } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ViewDayRoundedIcon from "@mui/icons-material/ViewDayRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import OwnerMonthGrid from "./calendar/OwnerMonthGrid";
import OwnerDaySheet from "./calendar/OwnerDaySheet";
import OwnerTaskDialog from "./calendar/OwnerTaskDialog";
import { OwnerGoogleCalendarDialog, OwnerGoogleEventDialog } from "./calendar/OwnerGoogleCalendarDialogs";
import OwnerRoutineDialog from "./calendar/OwnerRoutineDialog";

function localYmd(value = new Date()) { const date = new Date(value.getTime() - value.getTimezoneOffset() * 60000); return date.toISOString().slice(0, 10); }
function shiftDate(dateKey, delta) { const date = new Date(`${dateKey}T12:00:00`); date.setDate(date.getDate() + delta); return localYmd(date); }
function shiftMonth(month, delta) { const [year, monthNumber] = String(month).split("-").map(Number); const date = new Date(year, monthNumber - 1 + delta, 1, 12, 0, 0, 0); return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`; }
function monthLabel(month) { const [year, monthNumber] = String(month).split("-").map(Number); return new Intl.DateTimeFormat("es-AR", { month: "long", year: "numeric" }).format(new Date(year, monthNumber - 1, 1)); }
function shortDateLabel(dateKey) { const date = new Date(`${dateKey}T12:00:00`); return new Intl.DateTimeFormat("es-AR", { weekday: "short", day: "numeric", month: "short" }).format(date); }

export default function OwnerCalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = React.useMemo(() => localYmd(), []);
  const requestedDate = /^\d{4}-\d{2}-\d{2}$/.test(String(searchParams.get("fecha") || "")) ? String(searchParams.get("fecha")) : today;
  const requestedView = searchParams.get("vista") === "mes" ? "mes" : "dia";
  const [selectedDate, setSelectedDate] = React.useState(requestedDate);
  const [month, setMonth] = React.useState(requestedDate.slice(0, 7));
  const [view, setView] = React.useState(requestedView);
  const [calendar, setCalendar] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [dialog, setDialog] = React.useState("");
  const [exportingAgenda, setExportingAgenda] = React.useState(false);
  const [exportMessage, setExportMessage] = React.useState("");

  const syncUrl = React.useCallback((date, nextView = view) => {
    const next = new URLSearchParams(); next.set("fecha", date); if (nextView === "mes") next.set("vista", "mes"); setSearchParams(next, { replace: true });
  }, [setSearchParams, view]);

  const load = React.useCallback(async () => {
    setError("");
    try { const response = await ownerApi.calendar({ month, date: selectedDate }); setCalendar(response.calendar); }
    catch (err) { setError(formatApiError(err, "No pude cargar la agenda.")); }
    finally { setLoading(false); }
  }, [month, selectedDate]);
  React.useEffect(() => { load(); }, [load]);

  const selectDate = (date, forceDay = false) => {
    setSelectedDate(date); if (date.slice(0, 7) !== month) setMonth(date.slice(0, 7)); const nextView = forceDay ? "dia" : view; if (forceDay) setView("dia"); syncUrl(date, nextView);
  };
  const moveDay = (delta) => selectDate(shiftDate(selectedDate, delta), true);
  const moveMonth = (delta) => { const nextMonth = shiftMonth(month, delta); const nextDate = `${nextMonth}-01`; setMonth(nextMonth); setSelectedDate(nextDate); syncUrl(nextDate, "mes"); };
  const changeView = (nextView) => { setView(nextView); syncUrl(selectedDate, nextView); };

  // WP-STANDARD | AGENDA DIAGNOSTIC EXPORT
  // Status: UI-WIRED · READ-ONLY · SECRET-SAFE-BACKEND · TESTED-SYNTAX · v1
  // This intentionally exports only the Calendar/Inbox pipeline. The backend
  // redacts credentials first; the browser then removes unrelated Owner domains
  // so this file can be shared to debug cleanup/unification without exposing money.
  const exportAgendaDiagnostic = React.useCallback(async () => {
    setExportingAgenda(true);
    setExportMessage("");
    setError("");
    try {
      const response = await ownerApi.diagnosticExport({ daysBack:365, daysForward:365, limit:1000 });
      const diagnostic = response?.diagnostic || response || {};
      const agendaDiagnostic = {
        schema: "webprofit.owner.agenda-diagnostic",
        schemaVersion: 1,
        generatedAt: diagnostic.generatedAt || new Date().toISOString(),
        purpose: "Diagnóstico compartible del flujo Google Calendar -> Inbox/Commitments -> limpieza/materialización.",
        safety: diagnostic.safety || { secretsIncluded:false },
        exportWindow: diagnostic.exportWindow || null,
        runtimeContext: diagnostic.runtimeContext || null,
        authorityMap: diagnostic.authorityMap || null,
        origins: diagnostic.origins || null,
        calendarAndPlanning: diagnostic.calendarAndPlanning || null,
        capturesAndPendingHistory: diagnostic.capturesAndPendingHistory || null,
        learnedAndAudited: {
          decisionTraces: diagnostic.learnedAndAudited?.decisionTraces || null,
          actionAudits: diagnostic.learnedAndAudited?.actionAudits || null,
          technicalIncidents: diagnostic.learnedAndAudited?.technicalIncidents || null,
        },
        uiSnapshot: {
          selectedDate,
          month,
          view,
          currentAgenda: calendar,
          href: window.location.href,
        },
      };
      const blob = new Blob([JSON.stringify(agendaDiagnostic, null, 2)], { type:"application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      anchor.href = url;
      anchor.download = `webprofit-agenda-diagnostico-${stamp}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setExportMessage("Agenda exportada. Subime ese JSON y puedo revisar exactamente qué evento entra, qué se materializa y qué no se está limpiando.");
    } catch (err) {
      setError(formatApiError(err, "No pude exportar el diagnóstico de Agenda."));
    } finally {
      setExportingAgenda(false);
    }
  }, [calendar, month, selectedDate, view]);

  if (loading && !calendar) return <DashboardLoadingState label="Cargando agenda…" minHeight={520} />;

  return <Box sx={{ width:"100%", height:{ xs:"auto", md:"100%" }, minHeight:0, display:"flex", flexDirection:"column", overflow:{ xs:"visible", md:"hidden" } }}>
    {view === "mes" ? <Box sx={{ flex:"0 0 auto", mb:.65, px:{xs:.55,sm:.8}, py:.42, border:"1px solid", borderColor:"divider", borderRadius:2.2, bgcolor:"background.paper" }}>
      <Stack direction="row" spacing={.45} alignItems="center" sx={{minWidth:0}}>
        <Button size="small" variant="text" onClick={()=>changeView("dia")} startIcon={<ViewDayRoundedIcon/>} sx={{textTransform:"none",fontWeight:900}}>Día</Button>
        <Chip size="small" variant="outlined" color={calendar?.googleCalendar?.status === "error" ? "error" : calendar?.googleCalendar?.connected ? "success" : "default"} icon={<CalendarMonthRoundedIcon sx={{fontSize:"15px !important"}}/>} label={calendar?.googleCalendar?.connected ? "Google" : "Conectar Google"} onClick={()=>setDialog("google")} sx={{fontWeight:850}}/>
        <Chip size="small" variant="outlined" icon={<AutorenewRoundedIcon sx={{fontSize:"15px !important"}}/>} label="Rutina" onClick={()=>setDialog("routine")} sx={{fontWeight:850}}/>
        <Box sx={{flex:1}}/>
        <IconButton size="small" aria-label="Anterior" onClick={()=>moveMonth(-1)}><ChevronLeftRoundedIcon fontSize="small"/></IconButton>
        <Typography noWrap sx={{minWidth:135,textAlign:"center",textTransform:"capitalize",fontWeight:950,fontSize:12}}>{monthLabel(month)}</Typography>
        <IconButton size="small" aria-label="Siguiente" onClick={()=>moveMonth(1)}><ChevronRightRoundedIcon fontSize="small"/></IconButton>
        <IconButton size="small" aria-label="Hoy" onClick={()=>selectDate(today,true)}><TodayRoundedIcon fontSize="small"/></IconButton>
      </Stack>
    </Box> : null}
    {error ? <Alert severity="error" sx={{flex:"0 0 auto",mb:.55}}>{error}</Alert> : null}
    {exportMessage ? <Alert severity="success" onClose={()=>setExportMessage("")} sx={{flex:"0 0 auto",mb:.55}}>{exportMessage}</Alert> : null}
    {searchParams.get("google") === "connected" ? <Alert severity="success" sx={{flex:"0 0 auto",mb:.55}}>Google Calendar conectado.</Alert> : null}
    {calendar ? view === "mes"
      ? <Box sx={{flex:"1 1 auto",minHeight:0,overflow:{xs:"visible",md:"auto"}}}><OwnerMonthGrid month={calendar.month} days={calendar.days || []} selectedDate={calendar.selectedDate} onSelectDate={(date)=>selectDate(date,true)}/></Box>
      : <Box sx={{flex:"1 1 auto",minHeight:0,overflow:"visible"}}><OwnerDaySheet
          calendar={calendar}
          onNewTask={()=>setDialog("task")}
          onNewGoogleEvent={()=>setDialog("googleEvent")}
          onGoogleSettings={()=>setDialog("google")}
          onRoutine={()=>setDialog("routine")}
          onExportAgenda={exportAgendaDiagnostic}
          exportingAgenda={exportingAgenda}
          onPrevDay={()=>moveDay(-1)}
          onNextDay={()=>moveDay(1)}
          onToday={()=>selectDate(today,true)}
          onMonthView={()=>changeView("mes")}
          onChanged={load}
        /></Box>
      : null}
    <OwnerTaskDialog open={dialog === "task"} onClose={()=>setDialog("")} onSaved={load} selectedDate={selectedDate}/>
    <OwnerGoogleCalendarDialog open={dialog === "google"} onClose={()=>setDialog("")} currentIntegration={calendar?.googleCalendar} onChanged={load}/>
    <OwnerGoogleEventDialog open={dialog === "googleEvent"} onClose={()=>setDialog("")} selectedDate={selectedDate} onSaved={load}/>
    <OwnerRoutineDialog open={dialog === "routine"} onClose={()=>setDialog("")} calendar={calendar} onSaved={load}/>
  </Box>;
}
